import type { Track } from '../track/generate';
import type { RunResult } from '../game/session';
import { describeWorst } from '../game/analysis';
import { medalFor } from '../game/medals';
import { formatTime } from './hud';

const W = 1200;
const H = 630; // standard social preview ratio

/**
 * Renders the lap as a shareable PNG.
 *
 * A text result travels badly; an image of the circuit with your actual line on
 * it, coloured by speed, is the thing people post. Everything drawn here already
 * exists in the run result, so this costs one offscreen canvas.
 */
export function renderShareCard(
  track: Track,
  result: RunResult,
  meta: { dateKey: string; mode: string; weather: string; liveryColor: string },
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // --- background --------------------------------------------------------
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#0d1014');
  bg.addColorStop(1, '#141a20');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // --- track, right-hand side -------------------------------------------
  const panelX = 640;
  const panelW = W - panelX - 60;
  const b = track.bounds;
  const spanX = b.maxX - b.minX;
  const spanY = b.maxY - b.minY;
  const scale = Math.min(panelW / spanX, (H - 140) / spanY);
  const ox = panelX + (panelW - spanX * scale) / 2;
  const oy = 70 + (H - 140 - spanY * scale) / 2;
  const tx = (x: number): number => ox + (x - b.minX) * scale;
  const ty = (y: number): number => oy + (y - b.minY) * scale;

  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  // Track bed
  ctx.strokeStyle = '#242a32';
  ctx.lineWidth = track.width * scale;
  ctx.beginPath();
  ctx.moveTo(tx(track.center[0].x), ty(track.center[0].y));
  for (let i = 1; i < track.center.length; i++) {
    ctx.lineTo(tx(track.center[i].x), ty(track.center[i].y));
  }
  ctx.closePath();
  ctx.stroke();

  // The driven line, coloured by speed. This is the part worth looking at.
  const g = result.ghost;
  if (g.x.length > 1) {
    let vMin = Infinity;
    let vMax = 0;
    for (const v of g.speed) {
      vMin = Math.min(vMin, v);
      vMax = Math.max(vMax, v);
    }
    ctx.lineWidth = Math.max(3, track.width * scale * 0.42);
    for (let i = 0; i < g.x.length - 1; i++) {
      const t = (g.speed[i] - vMin) / Math.max(1e-6, vMax - vMin);
      // Blue where slow, through to white-hot on the straights.
      ctx.strokeStyle = `hsl(${210 - t * 190}, ${70 + t * 25}%, ${45 + t * 22}%)`;
      ctx.beginPath();
      ctx.moveTo(tx(g.x[i]), ty(g.y[i]));
      ctx.lineTo(tx(g.x[i + 1]), ty(g.y[i + 1]));
      ctx.stroke();
    }
  }

  // Start line
  const s = track.center[track.startIndex];
  const nrm = track.normal[track.startIndex];
  const half = (track.width / 2) * scale;
  ctx.strokeStyle = '#eef1f5';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(tx(s.x) + nrm.x * half, ty(s.y) + nrm.y * half);
  ctx.lineTo(tx(s.x) - nrm.x * half, ty(s.y) - nrm.y * half);
  ctx.stroke();

  // --- text, left-hand side ---------------------------------------------
  const mono = 'ui-monospace, SFMono-Regular, Menlo, monospace';
  const sans = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

  ctx.fillStyle = meta.liveryColor;
  ctx.fillRect(60, 70, 6, 40);

  ctx.fillStyle = '#8b95a5';
  ctx.font = `700 15px ${mono}`;
  ctx.fillText('DAILY KARTING HOTLAP', 82, 88);
  ctx.fillStyle = '#eef1f5';
  ctx.font = `600 22px ${sans}`;
  ctx.fillText(meta.dateKey, 82, 112);

  ctx.fillStyle = '#eef1f5';
  ctx.font = `700 104px ${mono}`;
  ctx.fillText(formatTime(result.timeMs), 60, 232);

  // Medal badge beside the time — the headline achievement, not the raw number.
  const medal = medalFor(result.timeMs, result.idealMs);
  if (medal.id !== 'none') {
    const timeW = ctx.measureText(formatTime(result.timeMs)).width;
    ctx.fillStyle = medal.color;
    ctx.beginPath();
    ctx.roundRect(74 + timeW, 176, 132, 46, 10);
    ctx.fill();
    ctx.fillStyle = '#14161a';
    ctx.font = `700 24px ${sans}`;
    ctx.textAlign = 'center';
    ctx.fillText(medal.name.toUpperCase(), 74 + timeW + 66, 208);
    ctx.textAlign = 'left';
  }

  const gap = (result.timeMs - result.idealMs) / 1000;
  ctx.fillStyle = '#ff8a3d';
  ctx.font = `700 30px ${mono}`;
  ctx.fillText(`+${gap.toFixed(2)}s off ideal`, 62, 278);

  // Sector strip
  ctx.fillStyle = '#8b95a5';
  ctx.font = `700 13px ${mono}`;
  ctx.fillText('SECTORS', 62, 330);
  let prev = 0;
  for (let i = 0; i < result.sectorMs.length; i++) {
    const split = result.sectorMs[i] - prev;
    prev = result.sectorMs[i];
    const x = 62 + i * 150;
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    ctx.fillRect(x, 344, 136, 46);
    ctx.fillStyle = '#eef1f5';
    ctx.font = `700 22px ${mono}`;
    ctx.fillText(formatTime(split), x + 12, 374);
  }

  // Conditions
  ctx.fillStyle = '#8b95a5';
  ctx.font = `600 17px ${sans}`;
  ctx.fillText(
    `${meta.mode} · ${meta.weather} · ${track.length.toFixed(0)}m · ${Math.round(result.maxSpeed * 3.6)} km/h peak`,
    62,
    436,
  );

  if (result.worst) {
    ctx.fillStyle = 'rgba(255,138,61,0.12)';
    ctx.fillRect(60, 462, 520, 52);
    ctx.fillStyle = '#ff8a3d';
    ctx.fillRect(60, 462, 4, 52);
    ctx.fillStyle = '#eef1f5';
    ctx.font = `600 18px ${sans}`;
    ctx.fillText(describeWorst(result.worst), 80, 494);
  }

  ctx.fillStyle = '#4d5663';
  ctx.font = `600 15px ${mono}`;
  ctx.fillText('a new circuit every day', 62, 566);

  return canvas;
}

export async function shareCardBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png'));
}
